// validationSchema.js
import * as yup from 'yup';
import { PASSWORD_MIN_LENGTH, PASSWORD_RULES } from '@shared/signup/passwordRules';
import { isValidProfileHeightCm, parseHeightDisplay } from '../utils/heightUnits';
// https://blog.logrocket.com/react-native-form-validations-with-formik-and-yup/
/* With this variation for phone numbers:

  The most popular pattern used for US-based phone numbers typically includes an area code,
  a three-digit exchange code, and a four-digit subscriber number. This is often represented
  in one of the following formats:

  (123) 456-7890
  123-456-7890
  123.456.7890
  123 456 7890
 */
export const signUpValidationSchema = yup.object().shape({
    fullName: yup
        .string()
        .matches(/(\w.+\s).+/, 'Enter at least 2 names')
        .required('Full name is required'),
    birthDate: yup
        .string()
        .required('Date of birth is required')
        .test('valid-format', 'Date must be in MM-DD-YYYY format', value => {
        const regex = /^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])-\d{4}$/;
        return regex.test(value || '');
    })
        .test('valid-date', 'Enter a valid date', value => {
        if (!value) {
            return false;
        }
        const parts = value.split('-');
        if (parts.length !== 3) {
            return false;
        }
        const [month, day, year] = parts.map(Number);
        if ([month, day, year].some(isNaN)) {
            return false;
        }
        const date = new Date(year, month - 1, day);
        return (date.getFullYear() === year &&
            date.getMonth() === month - 1 &&
            date.getDate() === day);
    })
        .test('not-in-future', 'Date of birth cannot be in the future', value => {
        if (!value) {
            return false;
        }
        const parts = value.split('-');
        if (parts.length !== 3) {
            return false;
        }
        const [month, day, year] = parts.map(Number);
        if ([month, day, year].some(isNaN)) {
            return false;
        }
        const date = new Date(year, month - 1, day);
        if (isNaN(date.getTime())) {
            return false;
        }
        return date <= new Date();
    })
        .test('age', 'You must be at least 18 years old', value => {
        if (!value) {
            return false;
        }
        const parts = value.split('-');
        if (parts.length !== 3) {
            return false;
        }
        const [month, day, year] = parts.map(Number);
        if ([month, day, year].some(isNaN)) {
            return false;
        }
        const birthDate = new Date(year, month - 1, day);
        if (isNaN(birthDate.getTime())) {
            return false;
        }
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const birthdayNotPassed = today.getMonth() < birthDate.getMonth() ||
            (today.getMonth() === birthDate.getMonth() &&
                today.getDate() < birthDate.getDate());
        if (birthdayNotPassed) {
            age--;
        }
        return age >= 18;
    }),
    gender: yup
        .string()
        .matches(/^[MF]$/)
        .required('Specify your gender'),
    phoneNumber: yup
        .string()
        .matches(/\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/, 'Enter a valid phone number')
        .required('Phone number is required'),
    email: yup
        .string()
        .email('Enter valid email')
        .required('An email address is required'),
    password: yup
        .string()
        .matches(PASSWORD_RULES[0].pattern, PASSWORD_RULES[0].message)
        .matches(PASSWORD_RULES[1].pattern, PASSWORD_RULES[1].message)
        .matches(PASSWORD_RULES[2].pattern, PASSWORD_RULES[2].message)
        .matches(PASSWORD_RULES[3].pattern, PASSWORD_RULES[3].message)
        .min(PASSWORD_MIN_LENGTH, ({ min }) => `Password must be at least ${min} characters`)
        .required('A password is required'),
    confirmPassword: yup
        .string()
        .oneOf([yup.ref('password')], 'Passwords do not match')
        .required('Confirm your password'),
    existingWeight: yup
        .number()
        .min(50, 'Minimum weight is 50')
        .required('Existing weight is required'),
    idealWeight: yup
        .number()
        .min(50, 'Minimum weight is 50')
        .required('Ideal weight is required'),
    heightDisplay: yup
        .string()
        .trim()
        .required('Height is required')
        .test('valid-height', "Enter height like 5'10", value => {
        if (!value) {
            return false;
        }
        const cm = parseHeightDisplay(value);
        return cm != null && isValidProfileHeightCm(cm);
    }),
    eatOutCount: yup.number(),
    sourceDesc: yup.string().required('Source is required'),
});
